import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Building, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { Client, CreateClientRequest, UpdateClientRequest } from '../../types/client';
import { clientService } from '../../services';

interface ClientModalProps {
  client?: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (client: Client) => void;
}

export const ClientModal: React.FC<ClientModalProps> = ({
  client,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<CreateClientRequest>({
    client_name: '',
    contact_email: '',
    phone: '',
    address: '',
    description: '',
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const isEditing = !!client;

  // Initialize form data when client changes
  useEffect(() => {
    if (client) {
      setFormData({
        client_name: client.client_name,
        contact_email: client.contact_email || '',
        phone: client.phone || '',
        address: client.address || '',
        description: client.description || '',
        is_active: client.is_active
      });
    } else {
      setFormData({
        client_name: '',
        contact_email: '',
        phone: '',
        address: '',
        description: '',
        is_active: true
      });
    }
    setError(null);
    setFieldErrors({});
  }, [client, isOpen]);

  const handleInputChange = (field: keyof CreateClientRequest, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field errors when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string[]> = {};

    if (!formData.client_name.trim()) {
      errors.client_name = ['Client name is required'];
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      errors.contact_email = ['Please enter a valid email address'];
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (isEditing && client) {
        response = await clientService.updateClient(client.id, formData as UpdateClientRequest);
      } else {
        response = await clientService.createClient(formData);
      }

      if (response.data) {
        onSave(response.data);
        onClose();
      } else {
        setError(response.message || 'Failed to save client');
      }
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setFieldErrors(err.response.data.errors);
      } else {
        setError(err.response?.data?.message || 'Failed to save client');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-lg p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <Building className="h-6 w-6 text-primary-600" />
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? 'Edit Client' : 'Create New Client'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Client Name */}
            <div>
              <label className="label flex items-center space-x-2">
                <Building className="h-4 w-4 text-gray-500" />
                <span>Client Name *</span>
              </label>
              <input
                type="text"
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                className={`input ${fieldErrors.client_name ? 'border-red-300' : ''}`}
                placeholder="Enter client name"
                required
              />
              {fieldErrors.client_name && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.client_name[0]}</p>
              )}
            </div>

            {/* Contact Email */}
            <div>
              <label className="label flex items-center space-x-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <span>Contact Email</span>
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                className={`input ${fieldErrors.contact_email ? 'border-red-300' : ''}`}
                placeholder="Enter contact email"
              />
              {fieldErrors.contact_email && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.contact_email[0]}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="label flex items-center space-x-2">
                <Phone className="h-4 w-4 text-gray-500" />
                <span>Phone</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`input ${fieldErrors.phone ? 'border-red-300' : ''}`}
                placeholder="Enter phone number"
              />
              {fieldErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.phone[0]}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="label flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-500" />
                <span>Address</span>
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`input ${fieldErrors.address ? 'border-red-300' : ''}`}
                rows={3}
                placeholder="Enter address"
              />
              {fieldErrors.address && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.address[0]}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="label flex items-center space-x-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span>Description</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className={`input ${fieldErrors.description ? 'border-red-300' : ''}`}
                rows={3}
                placeholder="Enter description or notes about the client"
              />
              {fieldErrors.description && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.description[0]}</p>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active Client
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-7">
              Inactive clients cannot have new accounts assigned to them
            </p>
          </form>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.client_name.trim()}
              className="btn btn-primary flex items-center space-x-2"
            >
              {loading && <div className="loading-spinner" />}
              <Save className="w-4 h-4" />
              <span>{isEditing ? 'Update' : 'Create'} Client</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};